import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({
  mode: "auto", // 'auto' | 'light' | 'dark'
  resolved: "dark",
  setMode: () => {},
});

const resolveAuto = () => {
  // 1) If system has explicit preference, use it
  if (typeof window !== "undefined" && window.matchMedia) {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    if (mql.matches) return "dark";
    const mqlLight = window.matchMedia("(prefers-color-scheme: light)");
    if (mqlLight.matches) return "light";
  }
  // 2) Otherwise fall back to local time (7h–19h = light)
  const h = new Date().getHours();
  return h >= 7 && h < 19 ? "light" : "dark";
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    try {
      const saved = localStorage.getItem("smk_theme");
      return saved === "light" || saved === "dark" || saved === "auto" ? saved : "auto";
    } catch {
      return "auto";
    }
  });

  const [resolved, setResolved] = useState(() =>
    mode === "auto" ? resolveAuto() : mode
  );

  const applyTheme = useCallback((value) => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", value);
      document.documentElement.style.colorScheme = value;
    }
  }, []);

  useEffect(() => {
    const r = mode === "auto" ? resolveAuto() : mode;
    setResolved(r);
    applyTheme(r);
  }, [mode, applyTheme]);

  // React to system theme changes when in auto
  useEffect(() => {
    if (mode !== "auto" || typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const r = resolveAuto();
      setResolved(r);
      applyTheme(r);
    };
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, [mode, applyTheme]);

  // Re-evaluate at sunrise/sunset boundaries (every 30 min) when in auto
  useEffect(() => {
    if (mode !== "auto") return;
    const interval = setInterval(() => {
      const r = resolveAuto();
      setResolved((prev) => {
        if (prev !== r) applyTheme(r);
        return r;
      });
    }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [mode, applyTheme]);

  const setMode = (next) => {
    setModeState(next);
    try {
      localStorage.setItem("smk_theme", next);
    } catch {
      /* ignore */
    }
  };

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
